import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from '../../../components/svgs/icons/CheckIcon';
import { PriorityFlagIcon } from '../../../components/svgs/icons/PriorityFlagIcon';
import { THEME } from '../../Theme/theme';

interface IProps {
	toolCall: Api.ActionItemToolCall;
}

export function formatToolCallDueDate(dueDate: string | undefined, hasDueTime: boolean): string | null {
	if (!dueDate) {
		return null;
	}
	const date = new Date(dueDate);
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return format(date, hasDueTime ? 'EEE, MMM d, h:mm a' : 'EEE, MMM d');
}

/** Compact confirmation of an action item the assistant just created, updated, or completed. */
export const ActionItemToolCallCard: React.FC<IProps> = ({ toolCall }) => {
	const due = formatToolCallDueDate(toolCall.dueDate, toolCall.hasDueTime);
	const priority = toolCall.priority && toolCall.priority !== 'None' ? toolCall.priority : null;
	const details = [toolCall.contactName, due ? `Due ${due}` : null, priority ? `${priority} priority` : null].filter(
		Boolean
	);
	return (
		<Link
			className={css(styleSheet.card)}
			data-testid='action-item-tool-call-card'
			to={`/notes-action-items?activeTab=action-items&actionItemId=${encodeURIComponent(toolCall.actionItemId)}`}
		>
			<span className={css(styleSheet.status, toolCall.isCompleted && styleSheet.statusCompleted)} aria-hidden>
				{toolCall.isCompleted ? <CheckIcon width={12} height={12} /> : null}
			</span>
			<span className={css(styleSheet.body)}>
				<span className={css(styleSheet.title, toolCall.isCompleted && styleSheet.titleCompleted)}>
					{toolCall.description}
				</span>
				{details.length > 0 ? (
					<span className={css(styleSheet.details)}>
						{priority ? <PriorityFlagIcon width={12} height={12} /> : null}
						{details.join(' · ')}
					</span>
				) : null}
			</span>
			<span className={css(styleSheet.kicker)}>{toolCall.isCompleted ? 'Completed' : 'Action item'}</span>
		</Link>
	);
};

const styleSheet = StyleSheet.create({
	card: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
		marginTop: THEME.spacing.sm,
		color: THEME.colors.text.main,
		textDecoration: 'none',
		':hover': {
			borderColor: THEME.colors.icon.gray,
		},
	},
	status: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 18,
		height: 18,
		borderRadius: '50%',
		border: `2px solid ${THEME.colors.icon.gray}`,
		flexShrink: 0,
		boxSizing: 'border-box',
	},
	statusCompleted: {
		backgroundColor: THEME.colors.icon.gray,
		color: THEME.colors.background.main,
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		flex: 1,
		minWidth: 0,
	},
	title: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	titleCompleted: {
		textDecoration: 'line-through',
		color: THEME.colors.text.light,
	},
	details: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	kicker: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
	},
});
